import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from "@angular/router";
import { of, Subject } from "rxjs";
import { catchError, finalize, switchMap, takeUntil, tap } from "rxjs/operators";
import { MessageService } from "primeng/api";

// Services / Subjects
import { TagsService } from "../../../services/tags.service";
import { PropertyService } from "../../../services/property.service";
import { PropertyInitialSubject } from "../../../subject/property.initial.subject";
import { FlowModeSubject } from "../../../subject/flow.mode.subject";
import { FlowService } from "../../../services/flow.service";
import { FlowStateService } from "../../../services/flow.state.service";
import { SegmentSubjectService } from "../../../subject/segment.subject";

// DTOs / Enums
import { TagInformationDto } from "../../../dto/tag.information.dto";
import { PropertyInitialResponseDto } from "../../../dto/property.initial.response.dto";
import { FlowMode } from "../../../dto/enum/flow.mode.enum";
import { RecordStageEnum } from "../../../dto/enum/record.stage.enum";

const STEP_KEY = 'tags';
const REQUIRED_COUNT = 3;

@Component({
    selector: 'app-property-tags',
    templateUrl: './property-tags.component.html',
    styleUrls: ['./property-tags.component.scss']
})
export class PropertyTagsComponent implements OnInit, OnDestroy {

    // --------- Flujo / navegación -------------
    currentMode: FlowMode = FlowMode.CREATE;
    previousUrl = '';
    nextUrl = '';

    // --------- Propiedad / backend -------------
    propertyInitial: PropertyInitialResponseDto | null = null;
    propertyId: number | undefined;
    segmentLabel: string | undefined = '';

    // --------- UI / estado -------------
    loading = true;
    showErrorDialog = false;
    dialogCode = '';
    dialogMessage = '';
    private destroy$ = new Subject<void>();

    // --------- Datos del paso -------------
    tags: TagInformationDto[] = [];
    hadExistingTags = false; // si ya tenía tags en backend (para decidir save vs update)

    constructor(
        private router: Router,
        private tagService: TagsService,
        private propertyService: PropertyService,
        private propertyInitialSubject: PropertyInitialSubject,
        private flowModeSubject: FlowModeSubject,
        private flowService: FlowService,
        private flowState: FlowStateService,
        private segmentSubject: SegmentSubjectService,
        private messageService: MessageService
    ) {}

    // -----------------------------------------------------------
    // Ciclo de vida
    // -----------------------------------------------------------
    ngOnInit(): void {
        // 1) Modo y rutas
        const mode = this.flowModeSubject.getCurrentMode();
        if (!mode) { this.router.navigate(['/list-pending']); return; }
        this.currentMode = mode;
        this.previousUrl = this.flowService.getPreviousStep(STEP_KEY);
        this.nextUrl = this.flowService.getNextStep(STEP_KEY);

        // 2) Paso actual
        this.flowState.setCurrentStep(STEP_KEY);

        // 3) Propiedad y stage
        this.propertyInitial = this.propertyInitialSubject.getCurrentProperty();
        if (!this.propertyInitial?.propertyId) {
            this.router.navigate(['/list-pending']);
            return;
        }
        this.propertyId = this.propertyInitial.propertyId;
        this.segmentLabel = this.resolveSegmentLabel();
        if (this.propertyInitial.recordStage) {
            this.flowState.setRecordStage(this.propertyInitial.recordStage as RecordStageEnum);
        }
        this.flowState.setPropertyId(this.propertyId);

        // 4) Cargar tags y aplicar cache
        this.loadTagsFlow();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // -----------------------------------------------------------
    // Carga de tags (catálogo + ya seleccionados)
    // -----------------------------------------------------------
    private loadTagsFlow(): void {
        if (!this.propertyId) return;
        this.loading = true;

        this.tagService.getTagsList(this.propertyId).pipe(
            tap((response: any[]) => {
                this.tags = (response || []).map((t: any) => ({ ...t, selected: false }));
            }),
            switchMap(() => this.tagService.getTagsListProperty(this.propertyId!)),
            tap((selected: TagInformationDto[]) => {
                this.hadExistingTags = Array.isArray(selected) && selected.length > 0;
                // Marcar seleccionados devueltos por backend
                (selected || []).forEach(sel => {
                    const found = this.tags.find(t => t.tagId === sel.tagId);
                    if (found) found.selected = true;
                });
                // Aplicar cache local (override)
                this.applyCache();
            }),
            finalize(() => (this.loading = false)),
            catchError((error) => {
                console.error('Error al cargar etiquetas:', error);
                this.tags = [];
                return of([]);
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private resolveSegmentLabel(): string {
        return this.propertyInitial?.segmentLabel
            || this.segmentSubject.getSelectedSegment()?.segmentLabel
            || 'propiedad';
    }

    // -----------------------------------------------------------
    // Cache local (FlowState)
    // -----------------------------------------------------------
    private persistToFlowState(): void {
        this.flowState.setStepData(STEP_KEY, {
            selectedTagIds: this.getSelectedTagIds()
        });
    }

    private applyCache(): void {
        const cached = this.flowState.getStepData(STEP_KEY);
        const ids: number[] = cached?.selectedTagIds || [];
        if (!ids.length) return;

        // limpia y aplica
        this.tags.forEach(t => t.selected = false);
        // si cache tiene > REQUIRED_COUNT, limitamos a los primeros 3
        ids.slice(0, REQUIRED_COUNT).forEach(id => {
            const found = this.tags.find(t => t.tagId === id);
            if (found) found.selected = true;
        });
    }

    private getSelectedTagIds(): number[] {
        return this.tags.filter(t => !!t.selected).map(t => t.tagId);
    }

    private selectedCount(): number {
        return this.getSelectedTagIds().length;
    }

    // -----------------------------------------------------------
    // UI
    // -----------------------------------------------------------
    toggleTagSelection(tag: TagInformationDto): void {
        if (tag.selected) {
            tag.selected = false;
        } else {
            if (this.selectedCount() >= REQUIRED_COUNT) {
                this.showError('TADIC-0010', `Debes seleccionar solo ${REQUIRED_COUNT} etiquetas.`);
                return;
            }
            tag.selected = true;
        }
        this.showErrorDialog = false;
        this.persistToFlowState();
    }

    // -----------------------------------------------------------
    // Guardado + avance de etapa
    // -----------------------------------------------------------
    private saveTags$() {
        const selectedIds = this.getSelectedTagIds();
        if (!this.propertyId) return of(null);

        // decide endpoint: si ya tenía, actualiza; si no, guarda
        const req$ = this.hadExistingTags
            ? this.tagService.updateTagsToProperty(this.propertyId, selectedIds)
            : this.tagService.saveTagsToProperty(this.propertyId, selectedIds);

        // marca stage local (el backend lo moverá en nextStage si corresponde)
        return req$.pipe(
            tap(() => this.flowState.setRecordStage(RecordStageEnum.TAGS)),
            catchError((error) => {
                console.error('Error al guardar/actualizar etiquetas:', error);
                this.showError('TADIC-0020', 'Error al guardar las etiquetas.');
                return of(null);
            })
        );
    }

    private shouldAdvanceStage(): boolean {
        if (this.currentMode === FlowMode.EDIT) return false;
        if (this.currentMode === FlowMode.CREATE) return true;

        // RESUME
        const stage = this.flowState.recordStage;
        if (!stage) return false;

        const stageStep = this.flowState.getStepByRecordStage(stage);
        const currentIdx = this.flowService.getStepIndex(STEP_KEY);
        const stageIdx = this.flowService.getStepIndex(stageStep);

        if (currentIdx === -1 || stageIdx === -1) return false;
        return currentIdx > stageIdx; // estamos después → avanzamos
    }

    // -----------------------------------------------------------
    // Botonera
    // -----------------------------------------------------------
    handleNextStep(): void {
        if (this.selectedCount() !== REQUIRED_COUNT) {
            this.showError('TADIC-0011', `Debes seleccionar ${REQUIRED_COUNT} etiquetas antes de continuar.`);
            return;
        }

        this.persistToFlowState();

        this.saveTags$().pipe(
            switchMap(() => {
                if (this.shouldAdvanceStage()) {
                    const body = { id: this.propertyId };
                    return this.propertyService.nextStageProperty(body).pipe(
                        catchError((err) => { console.error('nextStage error', err); return of(null); })
                    );
                }
                return of(null);
            }),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate([this.nextUrl]));
    }

    handleSaveAndExit(): void {
        // Permitir guardar parcial (0..3) y salir
        this.persistToFlowState();
        this.saveTags$().pipe(
            catchError(() => of(null)),
            takeUntil(this.destroy$)
        ).subscribe(() => this.router.navigate(['/list-pending']));
    }

    handlePreviousStep(): void {
        this.persistToFlowState();
        this.router.navigate([this.previousUrl]);
    }

    // -----------------------------------------------------------
    // Errores
    // -----------------------------------------------------------
    private showError(code: string, msg: string): void {
        this.showErrorDialog = true;
        this.dialogCode = code;
        this.dialogMessage = msg;
        this.messageService.add({ severity: 'error', summary: code, detail: msg });
    }
}
